import D from "@/admin/dashboard/Dashboard.module.css";
import BD from "@/components/Badge.module.css";

import { usageBasedRevenueQueryAtom } from "@/admin/accounts/accountsQuery.ts";
import { AdminLocationMenu } from "@/admin/AdminLocationSelect.tsx";
import { AdminMultiSelect, type MultiSelectItem } from "@/admin/AdminMultiSelect.tsx";
import { getPredictionGradient } from "@/admin/dashboard/getPredictionGradient.ts";
import { HeadingBlock } from "@/admin/dashboard/HeadingBlock.tsx";
import { LightBlock } from "@/admin/dashboard/LightBlock.tsx";
import { PercentageChange } from "@/admin/dashboard/PercentageChange.tsx";
import { abbreviateNumber, getHoursPassedAndLeft } from "@/admin/dashboard/utils.ts";
import { monthNames, WBarChart, type WBarChartOptions } from "@/admin/dashboard/WBarChart.tsx";
import { useTooltipPortal } from "@/admin/utils/useTooltipPortal.tsx";
import { BadgeDot } from "@/components/Badge.tsx";
import { CurrencyBalance } from "@/components/l10n/Currency.tsx";
import { Separator } from "@/components/Separator.tsx";
import { useConfig } from "@/config.ts";
import { type ResourceType, resourceTypesEnabledAtom } from "@/modules/api/resourceTypes.tsx";
import { usePricingLocations } from "@/modules/location/locationQuery.ts";
import { useThemeVar } from "@/modules/theme/useTheme.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import type { BarSeriesOption } from "echarts/charts";
import { useAtomValue } from "jotai/react";
import { useMemo, useState } from "react";
import { Label } from "react-aria-components";
import { sortBy, sumBy } from "remeda";
import invariant from "tiny-invariant";

interface ProductType extends MultiSelectItem<string> {
    title: string;
    order: number;
    defaultChecked?: boolean;
}

const creditedWithoutVatKey = "credited_without_vat";

function UsageBasedRevenueSectionContent() {
    const split = true;
    const limitMonths = 12;

    const { kubernetesEnabled } = useConfig();
    const enabled = useAtomValue(resourceTypesEnabledAtom);

    const colorWhite = useThemeVar("color-white");
    const colorMuted = useThemeVar("color-muted");
    const colorTextHoverAlpha = useThemeVar("color-text-hover-alpha");
    const colorText = useThemeVar("color-text");
    const colorPrimary = useThemeVar("color-primary");
    const colorChartFree = useThemeVar("color-chart-free");

    const data = useSuspenseQueryAtom(usageBasedRevenueQueryAtom(split, limitMonths));
    const locations = usePricingLocations();

    const defaultLocations = useMemo(() => [...locations.map((item) => item.slug), ""], [locations]);

    const deletedLocations = [
        ...new Set(
            data
                .map((item) => item.location_slug)
                .filter((slug): slug is string => !!slug && !defaultLocations.includes(slug)),
        ),
    ];

    const [checkedLocations, setCheckedLocations] = useState<string[]>([...defaultLocations, ...deletedLocations]);

    const getDefaultTypes = (enabled: Record<ResourceType, boolean>, kubernetesEnabled: boolean) => {
        const types: ProductType[] = [{ id: "CUSTOM_PRODUCT", title: "Custom products", order: 14 }];

        if (enabled.bare_metal) {
            types.push({
                id: "BARE_METAL_LEASE",
                title: "Bare metal",
                order: 1,
            });
        }

        if (enabled.virtual_machine) {
            types.push(
                { id: "VM_COMPUTE", title: "VM compute", order: 2 },
                { id: "BLOCK_STORAGE", title: "VM disk", order: 3 },
                { id: "REPLICA_BACKUP", title: "Backup of disk", order: 4 },
                { id: "REPLICA_SNAPSHOT", title: "Snapshot of disk", order: 5 },
                { id: "LICENSE", title: "Windows license", order: 8 },
            );
        }

        if (enabled.ip_address) {
            types.push(
                { id: "ASSIGNED_FLOATING_IP", title: "Assigned floating IP", order: 6 },
                { id: "UNASSIGNED_FLOATING_IP", title: "Unassigned floating IP", order: 7 },
            );
        }

        if (enabled.bucket) {
            types.push({ id: "OBJECT_STORAGE", title: "Object storage", order: 12 });
        }

        if (enabled.load_balancer) {
            types.push({ id: "LOAD_BALANCER", title: "Load balancer", order: 13 });
        }

        if (enabled.managed_service) {
            types.push(
                { id: "MANAGED_SERVICE_mariadb", title: "Managed Service MariaDB", order: 9 },
                { id: "MANAGED_SERVICE_postgresql", title: "Managed Service PostgreSQL", order: 10 },
            );

            if (kubernetesEnabled) {
                types.push({ id: "MANAGED_SERVICE_kubernetes", title: "Managed Service Kubernetes", order: 11 });
            }
        }

        return sortBy(types, (l) => l.order);
    };

    const defaultTypes = useMemo(() => getDefaultTypes(enabled, kubernetesEnabled), [enabled, kubernetesEnabled]);

    const [checkedProductTypes, setCheckedProductTypes] = useState<string[]>(() =>
        defaultTypes.filter((t) => t.defaultChecked !== false).map((t) => t.id),
    );

    const locationProducts = data.filter((item) => checkedLocations.includes(item.location_slug ?? ""));
    const filteredProducts = locationProducts.filter((item) => checkedProductTypes.includes(item.product_type ?? ""));
    const filteredUsageCredited = locationProducts.filter((item) => item.type === creditedWithoutVatKey);

    const periodStartMonths: string[] = [];
    const xAxisData: string[] = [];
    const totalData: number[] = [];
    const warrenShareData: number[] = [];
    const usageCreditedData: number[] = [];
    const predictionData: number[] = [];
    const momData: number[] = [];
    let usagePrediction = 0;
    let warrenPrediction = 0;
    let momPrediction = 0;

    for (let iteratorMonths = limitMonths; iteratorMonths > 0; iteratorMonths--) {
        const generatedDate = new Date();
        generatedDate.setDate(1);
        generatedDate.setMonth(generatedDate.getMonth() - (iteratorMonths - 1));
        const month = monthNames[generatedDate.getMonth()];
        const year = generatedDate.getFullYear();
        let formattedDate = new Date(Date.UTC(year, generatedDate.getMonth(), 1, 0, 0, 0)).toISOString();
        formattedDate = `${formattedDate.split(".")[0]}Z`;
        periodStartMonths.push(formattedDate);
        xAxisData.push(`${month} ${year}`);
    }

    periodStartMonths.forEach((month, index) => {
        const total = sumBy(
            filteredProducts.filter((item) => item.period_start_month === month),
            (i) => i.subtotal,
        );
        totalData.push(total);

        const credited = sumBy(
            filteredUsageCredited.filter((item) => item.period_start_month === month),
            (i) => i.subtotal,
        );
        usageCreditedData.push(credited);

        warrenShareData.push(((total - (credited ? credited : 0)) / 100) * 20);

        const mom = totalData[index - 1] ? ((totalData[index] - totalData[index - 1]) / totalData[index - 1]) * 100 : 0;
        momData.push(mom);

        if (index === periodStartMonths.length - 1) {
            const hours = getHoursPassedAndLeft(month);
            usagePrediction = (total / hours.passed) * hours.left;
            warrenPrediction = (usagePrediction / 100) * 20;

            if (totalData[index - 1]) {
                momPrediction = ((total + usagePrediction - totalData[index - 1]) / totalData[index - 1]) * 100;
            }

            predictionData.push(usagePrediction);
        } else {
            predictionData.push(0);
        }
    });

    const usageLastTotal = totalData.length > 1 ? totalData[limitMonths - 2] : 0;
    const usagePrevTotal = totalData.length > 2 ? totalData[limitMonths - 3] : 0;
    const usageLastWarren = warrenShareData.length > 1 ? warrenShareData[limitMonths - 2] : 0;
    const usage3Last = totalData.slice(-5, -1);
    const usage3Prev = totalData.slice(-6, -2);
    const mom3Last =
        usage3Last.length > 1
            ? ((usage3Last[usage3Last.length - 1] / usage3Last[0]) ** (1 / (usage3Last.length - 1)) - 1) * 100
            : 0;
    const mom3Prev =
        usage3Prev.length > 1
            ? ((usage3Prev[usage3Prev.length - 1] / usage3Prev[0]) ** (1 / (usage3Prev.length - 1)) - 1) * 100
            : 0;
    const usage12Last = totalData.slice(-12, -1);
    const usage12Prev = totalData.slice(-13, -2);
    const mom12Last =
        usage12Last.length > 1
            ? ((usage12Last[usage12Last.length - 1] / usage12Last[0]) ** (1 / (usage12Last.length - 1)) - 1) * 100
            : 0;
    const mom12Prev =
        usage12Prev.length > 1
            ? ((usage12Prev[usage12Prev.length - 1] / usage12Prev[0]) ** (1 / (usage12Prev.length - 1)) - 1) * 100
            : 0;

    const { portal, formatter } = useTooltipPortal(({ name, value, seriesName, dataIndex }) => {
        invariant(typeof value === "number");
        return (
            <div>
                <div className="pb-1">
                    <b>{name}</b>
                </div>

                <BadgeDot color="color-chart-free">
                    {seriesName}:{" "}
                    <b>
                        <CurrencyBalance value={value} />
                    </b>
                </BadgeDot>

                <div className="py-1">
                    <Separator />
                </div>

                <BadgeDot color="color-chart-2">
                    Usage:{" "}
                    <b>
                        <CurrencyBalance value={totalData[dataIndex]} />
                    </b>
                </BadgeDot>

                {dataIndex === totalData.length - 1 && (
                    <BadgeDot color={BD.free}>
                        Usage prediction:{" "}
                        <b>
                            <CurrencyBalance value={totalData[dataIndex] + usagePrediction} />
                        </b>
                    </BadgeDot>
                )}

                {!!momData[dataIndex] && !(dataIndex === momData.length - 1) && (
                    <BadgeDot color="color-chart-3">
                        Usage MoM: <b>{momData[dataIndex].toFixed(1)}%</b>
                    </BadgeDot>
                )}

                {dataIndex === momData.length - 1 && (
                    <BadgeDot color="color-chart-3">
                        Usage MoM prediction: <b>{momPrediction ? momPrediction.toFixed(1) : "N/A"}%</b>
                    </BadgeDot>
                )}

                <div className="py-1">
                    <Separator />
                </div>

                {!!usageCreditedData[dataIndex] && (
                    <BadgeDot color={undefined} style={{ color: "#fdd835" }}>
                        Total amount credited:{" "}
                        <b>
                            <CurrencyBalance value={usageCreditedData[dataIndex]} />
                        </b>
                    </BadgeDot>
                )}

                <BadgeDot color="color-text">
                    Warren share:{" "}
                    <b>
                        <CurrencyBalance value={warrenShareData[dataIndex]} />
                    </b>
                </BadgeDot>

                {dataIndex === warrenShareData.length - 1 && (
                    <BadgeDot color={BD.prediction}>
                        Warren share prediction:{" "}
                        <b>
                            <CurrencyBalance value={warrenShareData[dataIndex] + warrenPrediction} />
                        </b>
                    </BadgeDot>
                )}
            </div>
        );
    });

    const seriesData = defaultTypes
        .filter((type) => checkedProductTypes.includes(type.id))
        .map((type): BarSeriesOption => {
            const items = filteredProducts.filter((item) => item.product_type === type.id);

            const data = periodStartMonths.map((month) =>
                sumBy(
                    items.filter((item) => item.period_start_month === month),
                    (i) => i.subtotal,
                ),
            );

            return {
                name: type.title,
                type: "bar",
                stack: "total",
                itemStyle: {
                    borderColor: "transparent",
                    borderWidth: 1,
                    color: colorPrimary,
                },
                emphasis: {
                    itemStyle: {
                        color: colorChartFree,
                    },
                },
                tooltip: { formatter },
                data,
            };
        });

    const predictionItem: BarSeriesOption = {
        name: "Projected addition",
        type: "bar",
        stack: "total",
        itemStyle: {
            borderColor: "transparent",
            borderWidth: 1,
            color: getPredictionGradient(colorPrimary, colorChartFree),
        },
        emphasis: {
            itemStyle: {
                color: colorChartFree,
            },
        },
        tooltip: { formatter },
        data: predictionData,
    };

    const options: WBarChartOptions = {
        tooltip: {
            trigger: "item",
            axisPointer: {
                type: "none",
            },
            borderWidth: 0,
            backgroundColor: colorWhite,
            textStyle: {
                color: colorText,
            },
            extraCssText: "box-shadow: 0 8px 20px rgba(0 0 0 / 10%);",
        },
        grid: {
            top: "5px",
            left: "5px",
            right: "5px",
            bottom: "5px",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: xAxisData,
            axisLine: {
                lineStyle: {
                    color: colorMuted,
                },
            },
            axisTick: {
                show: false,
            },
        },
        yAxis: {
            type: "value",
            axisLabel: {
                formatter: (value) => abbreviateNumber(value),
            },
            splitLine: {
                lineStyle: {
                    color: colorTextHoverAlpha,
                },
            },
        },
        series: [...seriesData, predictionItem],
    };

    function onChangeLocation(value: string[]) {
        setCheckedLocations(value);
    }

    return (
        <>
            {portal}
            <div className={D.ItemHeader}>
                <div>
                    <h2 className="font-size-heading">Usage Based Revenue</h2>
                    <p>Total resources used in monetary value</p>
                </div>

                <HeadingBlock
                    title="CMGR 12 months"
                    subtitle={mom12Last && mom12Last !== Infinity ? `${mom12Last.toFixed(2)}%` : "N/A"}
                    className="ml-auto"
                >
                    <PercentageChange last={mom12Last} prev={mom12Prev} />
                </HeadingBlock>

                <HeadingBlock
                    title="CMGR 3 months"
                    subtitle={mom3Last && mom3Last !== Infinity ? `${mom3Last.toFixed(2)}%` : "N/A"}
                >
                    <PercentageChange last={mom3Last} prev={mom3Prev} />
                </HeadingBlock>

                <HeadingBlock
                    title="Last MoM growth"
                    subtitle={<CurrencyBalance value={usageLastTotal - usagePrevTotal} />}
                >
                    <PercentageChange last={usageLastTotal} prev={usagePrevTotal} />
                </HeadingBlock>

                <HeadingBlock title="Last month total" subtitle={<CurrencyBalance value={usageLastTotal} />}>
                    <div>
                        <span className="color-muted">Warren</span>: <CurrencyBalance value={usageLastWarren} />
                    </div>
                </HeadingBlock>
            </div>

            <Separator />

            <div className="flex justify-between pt-4 pb-4">
                <div className="flex gap-1rem font-size-small">
                    <BadgeDot color="color-chart-2">Usage</BadgeDot>

                    <BadgeDot color={BD.free}>Usage prediction</BadgeDot>
                </div>

                <AdminLocationMenu
                    noLocationEnabled
                    deletedLocations={deletedLocations}
                    value={checkedLocations}
                    locations={locations}
                    onChange={onChangeLocation}
                />
            </div>

            <div className="flex items-center pb-3">
                <Label className="pr-1">Filter by product type:</Label>
                <AdminMultiSelect items={defaultTypes} value={checkedProductTypes} onChange={setCheckedProductTypes} />
            </div>

            <WBarChart options={options} />
        </>
    );
}

export function UsageBasedRevenueSection() {
    return (
        <LightBlock className={D.Wide} loadingHeight="480px">
            <UsageBasedRevenueSectionContent />
        </LightBlock>
    );
}
